import { ActionIcon, Box, Button, Divider, Flex, Group, Stack, Tooltip, useMantineTheme } from '@mantine/core';
import * as C from '../../../../ui/common/CommonStyles';
import DeviceBasicInfo from './ui/basicInfo';
import ClientInfo from './ui/clientInfo';
import PlanInfo from './ui/planInfo';
import ServiceExtensionHistory from './ui/serviceExtensionHistory';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useDisclosure } from '@mantine/hooks';
import Calculator from './ui/calculator';
import { IconCalculator, IconChevronLeft } from '@tabler/icons-react';
import ServiceChannelInfo from './ui/servicChannelInfo';
import type { Device } from '../../../../../shared/types/device';
import useGetDeviceDetails from '../common/hooks/useGetDeviceDetails';
import useDeleteDevice from '../common/hooks/useDeleteDevice';
import { showSuccessNotification } from '../../../../ui/notification';
import { useQueryClient } from '@tanstack/react-query';
import { queries } from '../../../../../shared/queries';

export default function DeviceDetailsContainer() {
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useMantineTheme();
  const [opened, { open, close }] = useDisclosure(false);
  const [searchParams] = useSearchParams();
  const mac = searchParams.get('mac') || '';
  const queryClient = useQueryClient();

  const { deviceDetails } = useGetDeviceDetails({ id: Number(id!) });
  const { mutate: deleteDevice } = useDeleteDevice();

  const onConfirmDelete = () => {
    Swal.fire({
      title: '디바이스 삭제',
      text: '정말로 디바이스를 삭제하시겠습니까?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: theme.colors.secondary[5],
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
    }).then((result) => {
      if (result.isConfirmed) {
        deleteDevice([mac], {
          onSuccess: () => {
            queryClient.invalidateQueries(queries.devices.list._def);
            navigate('/admin/management/device');
            showSuccessNotification('삭제 되었습니다.');
          },
        });
      }
    });
  };

  return (
    <div>
      <C.TitleBox>
        <div className="title">디바이스 상세</div>
      </C.TitleBox>
      <C.ListBox>
        <Box pos="relative" pt="xs">
          <Stack>
            <DeviceBasicInfo data={deviceDetails as Device} />
            <ClientInfo />
            <ServiceChannelInfo />
            <Flex gap="xl" direction={{ base: 'column', lg: 'row' }}>
              <PlanInfo planId={deviceDetails?.planId ?? 0} />
              <Box flex={1}>
                <ServiceExtensionHistory />
              </Box>
            </Flex>
          </Stack>
          <Divider mt="xl" mb="sm" />
          <Box pos="absolute" top={0} right={0}>
            <Button
              // disabled={!!deviceDetails?.shipment}
              color="primary.5"
              onClick={() => navigate(`/admin/management/device/register/${id}`)}
            >
              수정
            </Button>
          </Box>
          <Group justify="center">
            <Button
              variant="default"
              leftSection={<IconChevronLeft size={16} />}
              onClick={() => navigate('/admin/management/device')}
            >
              목록으로
            </Button>
            <Button color="secondary.5" disabled={!!deviceDetails?.shipment} onClick={onConfirmDelete}>
              삭제
            </Button>
          </Group>
          <Box pos="absolute" bottom={4} right={4}>
            <Tooltip label="데이터 사용량 계산기" withArrow>
              <ActionIcon
                variant="outline"
                color="primary.5"
                bg="#fff"
                size={60}
                radius="xl"
                onClick={open}
                style={{
                  boxShadow: '2px 4px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                <IconCalculator size={36} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          </Box>
        </Box>
        {opened && <Calculator opened={opened} onClose={close} />}
      </C.ListBox>
    </div>
  );
}
